import React, { useCallback, useEffect } from 'react';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import FormField from 'components/form/FormField.component';
import TextInput from 'components/form/TextInput.component';
import Space from 'components/generic/Space.component';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { formatDayOfWeek, formatDayToYear, formatDayToYearWeekday, formatHourAndMinute, formatHourToYear, formatPartDate, formatPartTime } from 'utils/date/format.util';
import { nowAsZulu } from 'utils/date/now.util';
import parseDate from 'utils/parsers/parseDate';
import createNodeKey from 'utils/react/createNodeKey.util';

// Since this is experiment, we can share the same .module.scss file
import CN from './TimeFormatsPage.module.scss';

const decamel = (name) => name.replace(/([a-z])([A-Z])/g, '$1 $2');

const FORMATTERS = Object.freeze({
  formatHourToYear,
  formatHourAndMinute,
  formatDayOfWeek,
  formatDayToYear,
  formatDayToYearWeekday,
  formatPartDate,
  formatPartTime,
});

const TimeFormatsPage = () => {
  const { val: isActive, inv: isInactive, tip: toggle } = useTip(false);
  const [date, setDate] = useStateWhileMounted(nowAsZulu());
  const handleDateChange = useCallback((ev) => setDate(ev.trimmed), [setDate]);

  useEffect(() => {
    // document.documentElement
    document.getElementById('root').setAttribute('data-theme', isActive ? 'dark' : 'light');
  }, [isActive]);

  const nextState = String(isInactive);

  const componentClasses = cn({
    [CN.component]: true,
    [CN.active]: isActive,
    [CN.inactive]: isInactive,
  });

  return (
    <div data-bem="TimeFormatsPage" className={componentClasses}>
      <h1 className={CN.title}>Time Formats Page</h1>

      <h2 className={CN.subtitle}>Input Panel</h2>
      <div data-bem="TimeFormats_age__form" className={CN.form}>
        <FormField input={TextInput} name="date" value={date} onChange={handleDateChange} />
        <Button variant="primary" casing="dialog" onClick={toggle}>
          Switch to {nextState}
        </Button>
      </div>

      <h2 className={CN.subtitle}>Formats Panel</h2>
      <div data-bem="TimeFormatsPage__table" className={CN.table}>
        {Object.entries(FORMATTERS).map(([formatter, format]) => (
          <React.Fragment key={createNodeKey({ unique: formatter })}>
            <div data-bem="TimeFormatsPage__name" className={CN.name}>
              <Space>{decamel(fromAnyToTrimmed(formatter)).replace(/^format\s/, '')}</Space>
              <Space>
                <code className={CN.code}>{formatter}</code>
              </Space>
            </div>
            <div data-bem="TimeFormatsPage__value" className={CN.value}>
              {format(parseDate(date))}
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default TimeFormatsPage;
